/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, fromEvent, merge, Observable, Subscription, timer } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { SessionTimeoutDialogComponent } from 'src/app/components/dialogs/session-timeout-dialog/session-timeout-dialog.component';
import { environment } from 'src/environments/environment';
import { TabHelperService } from './tab-helper.service';

export enum IdleUserTimes {
  PromptDialogBeforeLogoutInMins = environment.sessionTimeout.promptDialogBeforeLogoutInMins,
  RemainingTimeBeforeLogout = environment.sessionTimeout.remainingTimeBeforeLogout,
}

@Injectable({
  providedIn: 'root',
})
export class SessionTimeoutService implements OnDestroy {
  private idleTimeout = IdleUserTimes.PromptDialogBeforeLogoutInMins * 60 * 1000;
  private alertTimeout = IdleUserTimes.RemainingTimeBeforeLogout * 60 * 1000;
  private userActivity$: Observable<Event>;
  private inactivityCheckSubscription: Subscription;
  private userActivitySubscription: Subscription;
  private idleTimeoutSubscription: Subscription;
  private visibilityChangeSubscription: Subscription;
  private idleTimeoutDialogRef: MatDialogRef<SessionTimeoutDialogComponent, any>;

  constructor(public dialog: MatDialog, private authService: AuthService, private tabHelper: TabHelperService) {
    this.registerEvents();
    this.tabVisibilityChange();
  }

  public startInactivityCheck(): void {
    this.isInactivityCheckStarted() && this.inactivityCheckSubscription.unsubscribe();

    this.inactivityCheckSubscription = timer(this.idleTimeout).subscribe(() => {
      this.stopInactivityCheck();
      this.unRegisterEvents();

      // only an active tab or all inactive tabs can trigger the timeout
      if (!this.tabHelper.anyTabActive() || this.tabHelper.isCurrentTabActive()) {
        this.startIdleTimeout();
      }
    });
  }

  public stopInactivityCheck(): void {
    this.inactivityCheckSubscription.unsubscribe();
  }

  public restartInactivityCheck(): void {
    this.stopInactivityCheck();
    !this.isIdleTimeoutStarted() && this.startInactivityCheck();
    !this.areEventsRegistered() && this.registerEvents();
  }

  private isInactivityCheckStarted(): boolean {
    return this.inactivityCheckSubscription && !this.inactivityCheckSubscription.closed;
  }

  private registerEvents(): void {
    this.userActivity$ = merge(
      fromEvent(document, 'click'),
      fromEvent(document, 'scroll'),
      fromEvent(document, 'keydown'),
      fromEvent(document, 'mousemove')
    );
    this.userActivitySubscription = this.userActivity$.pipe(distinctUntilChanged(), debounceTime(500)).subscribe(() => {
      this.tabHelper.markTabAsActive();
      this.restartInactivityCheck();
    });
  }

  private unRegisterEvents(): void {
    this.userActivitySubscription.unsubscribe();
  }

  private areEventsRegistered(): boolean {
    return this.userActivitySubscription && !this.userActivitySubscription.closed;
  }

  private startIdleTimeout(): void {
    this.idleTimeoutSubscription = timer(this.alertTimeout).subscribe(() => {
      // only an active tab or all inactive tabs can logout
      if (!this.tabHelper.anyTabActive() || this.tabHelper.isCurrentTabActive()) {
        this.authService.signOut();
      }
    });

    if (this.idleTimeoutDialogRef?.getState() === MatDialogState.OPEN) return;

    this.idleTimeoutDialogRef = this.dialog.open(SessionTimeoutDialogComponent, {
      disableClose: true,
      width: '560px',
      maxWidth: 'calc(100vw - 64px)',
      data: {
        remainingTimeBeforeLogout: IdleUserTimes.RemainingTimeBeforeLogout,
      },
    });
    this.idleTimeoutDialogRef.afterClosed().subscribe((continued: boolean) => {
      if (continued) {
        this.stopIdleTimeout();
        this.startInactivityCheck();
        this.registerEvents();
      }
    });
  }

  private stopIdleTimeout(): void {
    this.idleTimeoutSubscription.unsubscribe();
  }

  private isIdleTimeoutStarted(): boolean {
    return this.idleTimeoutSubscription && !this.idleTimeoutSubscription.closed;
  }

  private tabVisibilityChange(): void {
    this.visibilityChangeSubscription = this.tabHelper.visibilityChange().subscribe({
      next: (visible: boolean) => {
        if (visible && !this.isInactivityCheckStarted() && !this.isIdleTimeoutStarted()) {
          this.restartInactivityCheck();
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.unRegisterEvents();
    this.stopIdleTimeout();
    this.stopInactivityCheck();
    this.visibilityChangeSubscription.unsubscribe();
  }
}
